import { Cartesian3 } from "cesium";
import { CurveDataType, } from "./interface";
import { calculateHACurveParameters } from "./horizontal-alignment-utils";
import { CLOTHOID_CURVE_TYPE } from "../../geometric-elements/clothoid";
import { NEAR_ZERO_THRESHOLD } from "../vertical-alignment/vertical-alignment";
var OverlapClassification;
(function (OverlapClassification) {
    OverlapClassification[OverlapClassification["STANDARD_LINEAR"] = 0] = "STANDARD_LINEAR";
    OverlapClassification[OverlapClassification["REQUIRE_PRC"] = 1] = "REQUIRE_PRC";
    OverlapClassification[OverlapClassification["REQUIRE_PCC"] = 2] = "REQUIRE_PCC";
})(OverlapClassification || (OverlapClassification = {}));
/**
 * Algebraic Solver for calculating precise spiral lengths that force
 * two adjacent curves to meet exactly at a tangent point (PRC)
 * in order to completely consume the available tangent distance D.
 */
function solveExactTangentOverlap(D, R1, delta1, Ls1_ideal, R2, delta2, Ls2_ideal) {
    const k = Ls2_ideal / (Ls1_ideal || 1e-6);
    const tanD1 = Math.tan(delta1 / 2);
    const tanD2 = Math.tan(delta2 / 2);
    const a = tanD1 / (24 * R1) + (Math.pow(k, 2) * tanD2) / (24 * R2);
    const b = 0.5 + k * 0.5;
    const c = R1 * tanD1 + R2 * tanD2 - D;
    if (c > 0) {
        throw new Error(`Fatal Geometry Error: The available tangent distance (${D.toFixed(2)}) is smaller than the minimum circular arc intersection distance (${c.toFixed(2)}).`);
    }
    const discriminant = Math.pow(b, 2) - 4 * a * c;
    if (discriminant < 0) {
        throw new Error("Geometric Solver failed: Impossible algebraic roots.");
    }
    const x = (-b + Math.sqrt(discriminant)) / (2 * a);
    return { Ls1: x, Ls2: k * x };
}
/**
 * Calculates a proportional bridge for Egg Curves (PCCs).
 * Since Egg curves do not touch R=infinity (Tangent), they dynamically
 * span between the two target curves sharing the remaining available curve distance.
 */
function solveExactPccOverlap(D, R1, delta1, Ls1_ideal, R2, delta2, Ls2_ideal) {
    // A PCC transitions natively from R1 to R2 without hitting tangent exactly.
    // We optimize the available tangents such that the connection happens efficiently inside the radii boundary.
    const k = Ls2_ideal / (Ls1_ideal || 1e-6);
    const tanD1 = Math.tan(delta1 / 2);
    const tanD2 = Math.tan(delta2 / 2);
    const a = tanD1 / (24 * R1) + (Math.pow(k, 2) * tanD2) / (24 * R2);
    const b = 0.5 + k * 0.5;
    const c = R1 * tanD1 + R2 * tanD2 - D;
    if (c > 0) {
        throw new Error(`Fatal Geometry Error: PCC Radius violation. The circular arcs physically intersect.`);
    }
    const discriminant = Math.pow(b, 2) - 4 * a * c;
    const x = (-b + Math.sqrt(discriminant)) / (2 * a);
    // Legg represents the continuous transitional clothoid length bridging R1 to R2 bridging the 'gap'.
    // As this is a tangent plane solver primarily, Legg approximates the arc transition perimeter equivalently.
    return { Ls1: x, Ls2: k * x, Legg: (x + k * x) / 2 };
}
function generatePointId(prefix, ipId) {
    return `${prefix}_${ipId}`;
}
export function generateCompositeAlignmentFromIPs(ips, speedKmH) {
    if (ips.length < 2) {
        throw new Error("At least 2 IP points are required.");
    }
    const curves = [];
    const numIps = ips.length;
    // Pass 1: Preliminary Computations for each Curve Independent State
    const ipGeometries = ips.map((ip, index) => {
        if (index === 0 || index === numIps - 1)
            return null;
        const startIP = ips[index - 1].point;
        const endIP = ips[index + 1].point;
        const v1 = Cartesian3.subtract(startIP, ip.point, new Cartesian3());
        const v2 = Cartesian3.subtract(endIP, ip.point, new Cartesian3());
        const unit1 = Cartesian3.normalize(v1, new Cartesian3());
        const unit2 = Cartesian3.normalize(v2, new Cartesian3());
        const dot = Cartesian3.dot(unit1, unit2);
        const delta = Math.PI - Math.acos(Math.max(-1, Math.min(1, dot)));
        if (delta < 1e-4)
            return null;
        const cross = Cartesian3.cross(unit1, unit2, new Cartesian3());
        const turnDirSign = Math.sign(cross.z) || 1;
        const dParams = calculateHACurveParameters(speedKmH);
        const R = ip.radius ?? dParams.R;
        const LsIn = ip.spiralLengthIn ?? dParams.Ls;
        const LsOut = ip.spiralLengthOut ?? dParams.Ls;
        return {
            ipPoint: ip.point,
            ipId: ip.id,
            unit1,
            unit2,
            delta,
            turnDirSign,
            R,
            LsIn,
            LsOut,
            distToPrevIP: Cartesian3.magnitude(v1),
            distToNextIP: Cartesian3.magnitude(v2),
            fatalOverreachIn: false,
            fatalOverreachOut: false,
            tInSolved: 0,
            tOutSolved: 0,
            clsRouteOut: OverlapClassification.STANDARD_LINEAR,
            leggOutResolved: 0,
        };
    });
    // Pass 2/3: Overlap Detection, Classification, and Constraint Solving
    for (let i = 1; i < numIps - 1; i++) {
        const curr = ipGeometries[i];
        const next = ipGeometries[i + 1];
        if (!curr)
            continue;
        let S_in = Math.pow(curr.LsIn, 2) / (24 * curr.R);
        let S_out = Math.pow(curr.LsOut, 2) / (24 * curr.R);
        const T_in = (curr.R + S_in) * Math.tan(curr.delta / 2) + curr.LsIn / 2;
        let T_out = (curr.R + S_out) * Math.tan(curr.delta / 2) + curr.LsOut / 2;
        if (T_in > curr.distToPrevIP)
            curr.fatalOverreachIn = true;
        if (T_out > curr.distToNextIP)
            curr.fatalOverreachOut = true;
        // Analyze overlap extending into the NEXT curve tangent distance
        if (next) {
            const distBetween = curr.distToNextIP;
            const nextS_in = Math.pow(next.LsIn, 2) / (24 * next.R);
            const nextT_in = (next.R + nextS_in) * Math.tan(next.delta / 2) + next.LsIn / 2;
            if (T_out + nextT_in >= distBetween) {
                let cls = OverlapClassification.REQUIRE_PCC;
                if (curr.turnDirSign !== next.turnDirSign) {
                    cls = OverlapClassification.REQUIRE_PRC; // S-Curve
                }
                curr.clsRouteOut = cls;
                try {
                    if (cls === OverlapClassification.REQUIRE_PRC) {
                        const { Ls1, Ls2 } = solveExactTangentOverlap(distBetween, curr.R, curr.delta, curr.LsOut, next.R, next.delta, next.LsIn);
                        curr.LsOut = Ls1;
                        next.LsIn = Ls2;
                    }
                    else {
                        // REQUIRE_PCC
                        const { Ls1, Ls2, Legg } = solveExactPccOverlap(distBetween, curr.R, curr.delta, curr.LsOut, next.R, next.delta, next.LsIn);
                        curr.LsOut = Ls1;
                        next.LsIn = Ls2;
                        curr.leggOutResolved = Legg;
                    }
                    S_out = Math.pow(curr.LsOut, 2) / (24 * curr.R);
                    T_out = (curr.R + S_out) * Math.tan(curr.delta / 2) + curr.LsOut / 2;
                }
                catch (err) {
                    const msg = err instanceof Error ? err.message : "Algebraic collision failure.";
                    throw new Error(msg);
                }
            }
        }
        S_in = Math.pow(curr.LsIn, 2) / (24 * curr.R);
        S_out = Math.pow(curr.LsOut, 2) / (24 * curr.R);
        curr.tInSolved = (curr.R + S_in) * Math.tan(curr.delta / 2) + curr.LsIn / 2;
        curr.tOutSolved =
            (curr.R + S_out) * Math.tan(curr.delta / 2) + curr.LsOut / 2;
        if (curr.tInSolved > curr.distToPrevIP) {
            throw new Error(`Fatal Geometric Error: Curve at IP ${curr.ipId} extends backwards.`);
        }
    }
    // Pass 4: Continuous Coordinates Assignment and Synthesis into THACurveData structures
    const resolvedPointsList = Array(numIps)
        .fill(null)
        .map(() => ({
        tIn: 0,
        tOut: 0,
        stPoint: null,
        tsPoint: null,
        components: [],
    }));
    resolvedPointsList[0].stPoint = ips[0].point;
    resolvedPointsList[numIps - 1].tsPoint = ips[numIps - 1].point;
    for (let i = 1; i < numIps - 1; i++) {
        const geom = ipGeometries[i];
        if (!geom)
            continue;
        const T_in = geom.tInSolved;
        const T_out = geom.tOutSolved;
        const tsPoint = Cartesian3.add(geom.ipPoint, Cartesian3.multiplyByScalar(geom.unit1, T_in, new Cartesian3()), new Cartesian3());
        const stPoint = Cartesian3.add(geom.ipPoint, Cartesian3.multiplyByScalar(geom.unit2, T_out, new Cartesian3()), new Cartesian3());
        resolvedPointsList[i].tIn = T_in;
        resolvedPointsList[i].tOut = T_out;
        resolvedPointsList[i].tsPoint = tsPoint;
        resolvedPointsList[i].stPoint = stPoint;
        const tsId = generatePointId("TS", geom.ipId);
        const scId = generatePointId("SC", geom.ipId);
        const csId = generatePointId("CS", geom.ipId);
        const stId = generatePointId("ST", geom.ipId);
        const arcAnchorId = geom.ipId;
        // If this curve is the trailing end of a PCC (meaning previous curve pushed an ARC_TO_ARC)
        // We skip generating the incoming clothoid because the ARC_TO_ARC bridged directly to this arc!
        const prevGeom = ipGeometries[i - 1];
        const isTargetOfPCC = prevGeom && prevGeom.clsRouteOut === OverlapClassification.REQUIRE_PCC;
        if (!isTargetOfPCC) {
            const clothoidIn = {
                type: CurveDataType.CLOTHOID,
                startPointId: tsId,
                endPointId: scId,
                clothoidType: CLOTHOID_CURVE_TYPE.STRAIGHT_TO_ARC,
                radius: geom.R,
                parameterA: Math.sqrt(geom.R * geom.LsIn),
                anchorPointId: arcAnchorId,
                length: geom.LsIn,
            };
            resolvedPointsList[i].components.push(clothoidIn);
        }
        const thetaS_in = geom.LsIn / (2 * geom.R);
        const thetaS_out = geom.LsOut / (2 * geom.R);
        const centralAngle = geom.delta - thetaS_in - thetaS_out;
        if (centralAngle > 1e-4) {
            // Instead of starting at generic scId, start at previous end if PCC bridged perfectly.
            const arrData = {
                type: CurveDataType.CIRCULAR_SEGMENT,
                startPointId: isTargetOfPCC
                    ? generatePointId("ARC_TO_ARC_END", prevGeom.ipId)
                    : scId,
                endPointId: csId,
                anchorPointId: arcAnchorId,
            };
            resolvedPointsList[i].components.push(arrData);
        }
        // What follows the Circular Arc?
        if (geom.clsRouteOut === OverlapClassification.REQUIRE_PCC &&
            ipGeometries[i + 1]) {
            // Instead of ARC_TO_STRAIGHT, push an ARC_TO_ARC (True Egg-Curve)
            const nextGeom = ipGeometries[i + 1];
            const arcToArcId = generatePointId("ARC_TO_ARC_END", geom.ipId);
            const clothoidEgg = {
                type: CurveDataType.CLOTHOID,
                startPointId: csId,
                endPointId: arcToArcId, // The next circular segment will start exactly here
                clothoidType: CLOTHOID_CURVE_TYPE.ARC_TO_ARC,
                radius: geom.R, // fallback base
                startRadius: geom.R,
                endRadius: nextGeom?.R, // true boundaries for downstream simulation
                parameterA: Math.sqrt(geom.R * geom.leggOutResolved),
                anchorPointId: csId,
                length: geom.leggOutResolved,
            };
            resolvedPointsList[i].components.push(clothoidEgg);
        }
        else {
            // Normal outgoing clothoid
            const clothoidOut = {
                type: CurveDataType.CLOTHOID,
                startPointId: centralAngle > 1e-4
                    ? csId
                    : isTargetOfPCC
                        ? generatePointId("ARC_TO_ARC_END", prevGeom?.ipId)
                        : scId,
                endPointId: stId,
                clothoidType: CLOTHOID_CURVE_TYPE.ARC_TO_STRAIGHT,
                radius: geom.R,
                parameterA: Math.sqrt(geom.R * geom.LsOut),
                anchorPointId: stId,
                length: geom.LsOut,
            };
            resolvedPointsList[i].components.push(clothoidOut);
        }
    }
    // Stitching into continuous alignment
    for (let i = 0; i < numIps - 1; i++) {
        if (resolvedPointsList[i].components.length > 0) {
            curves.push(...resolvedPointsList[i].components);
        }
        // Calculate straight line distance between ST[i] and TS[i+1]
        // But if it's a PCC, there isn't a straight line bridge at all!
        const geom = ipGeometries[i];
        if (geom && geom.clsRouteOut === OverlapClassification.REQUIRE_PCC) {
            // PCC natively overrides the straight bridge, the array is perfectly contiguous via ARC_TO_ARC string.
            continue;
        }
        const stInfo = resolvedPointsList[i];
        const tsInfo = resolvedPointsList[i + 1];
        const startPt = stInfo.stPoint || ips[i].point;
        const endPt = tsInfo.tsPoint || ips[i + 1].point;
        const lineDist = Cartesian3.magnitude(Cartesian3.subtract(endPt, startPt, new Cartesian3()));
        if (lineDist > 0.001) {
            const startId = stInfo.stPoint
                ? i === 0
                    ? ips[i].id
                    : generatePointId("ST", ips[i].id)
                : ips[i].id;
            const endId = tsInfo.tsPoint
                ? i === numIps - 2
                    ? ips[i + 1].id
                    : generatePointId("TS", ips[i + 1].id)
                : ips[i + 1].id;
            const linearSeg = {
                type: CurveDataType.LINEAR,
                startPointId: startId,
                endPointId: endId,
            };
            curves.push(linearSeg);
        }
    }
    if (resolvedPointsList[numIps - 1].components.length > 0) {
        curves.push(...resolvedPointsList[numIps - 1].components);
    }
    const newPoints = [];
    for (let i = 1; i < numIps - 1; i++) {
        const geom = ipGeometries[i];
        const pts = resolvedPointsList[i];
        if (!geom)
            continue;
        if (pts.tsPoint) {
            newPoints.push({
                id: generatePointId("TS", geom.ipId),
                point: pts.tsPoint,
            });
        }
        if (pts.stPoint) {
            newPoints.push({
                id: generatePointId("ST", geom.ipId),
                point: pts.stPoint,
            });
        }
    }
    const updatedIPs = ips.map((ip, i) => {
        const geom = ipGeometries[i];
        if (geom) {
            return {
                ...ip,
                radius: geom.R,
                spiralLengthIn: geom.LsIn,
                spiralLengthOut: geom.LsOut,
            };
        }
        return ip;
    });
    return { curves, newPoints, updatedIPs };
}
/**
 * Scales Radii proportionally when tangent distances collide for Simple Curves.
 */
function solveExactSimpleCurveOverlap(D, R1_ideal, delta1, R2_ideal, delta2) {
    const k = R2_ideal / (R1_ideal || 1e-6);
    const tan1 = Math.tan(delta1 / 2);
    const tan2 = Math.tan(delta2 / 2);
    // D = R1 * tan1 + (k * R1) * tan2
    const R1_scaled = D / (tan1 + k * tan2);
    return { R1: R1_scaled, R2: k * R1_scaled };
}
export function generateCompositeAlignmentFromIPs_CurveOnly(ips, speedKmH) {
    if (ips.length < 2) {
        throw new Error("At least 2 IP points are required.");
    }
    const curves = [];
    const numIps = ips.length;
    // Pass 1: Preliminary Computations for Independent State
    const ipGeometries = ips.map((ip, index) => {
        // if (index === 0 || index === numIps - 1) return null;
        const startIP = ips[index - 1]?.point;
        const endIP = ips[index + 1]?.point;
        if (startIP && endIP) {
            const v1 = Cartesian3.subtract(startIP, ip.point, new Cartesian3());
            const v2 = Cartesian3.subtract(endIP, ip.point, new Cartesian3());
            const unit1 = Cartesian3.normalize(v1, new Cartesian3());
            const unit2 = Cartesian3.normalize(v2, new Cartesian3());
            const dot = Cartesian3.dot(unit1, unit2);
            const delta = Math.PI - Math.acos(Math.max(-1, Math.min(1, dot)));
            if (delta < 1e-4)
                return null;
            const dParams = calculateHACurveParameters(speedKmH);
            const R = ip.radius ?? dParams.R;
            return {
                ipPoint: ip.point,
                ipId: ip.id,
                unit1,
                unit2,
                delta,
                R,
                distToPrevIP: Cartesian3.magnitude(v1),
                distToNextIP: Cartesian3.magnitude(v2),
                tInSolved: 0,
                tOutSolved: 0,
            };
        }
        else if (startIP && !endIP) {
            const v1 = Cartesian3.subtract(startIP, ip.point, new Cartesian3());
            const unit1 = Cartesian3.normalize(v1, new Cartesian3());
            return {
                ipPoint: ip.point,
                ipId: ip.id,
                unit1,
                unit2: null,
                delta: null,
                R: null,
                distToPrevIP: Cartesian3.magnitude(v1),
                distToNextIP: null,
                tInSolved: 0,
                tOutSolved: 0,
            };
        }
        else if (!startIP && endIP) {
            const v2 = Cartesian3.subtract(endIP, ip.point, new Cartesian3());
            const unit2 = Cartesian3.normalize(v2, new Cartesian3());
            return {
                ipPoint: ip.point,
                ipId: ip.id,
                unit1: null,
                unit2,
                delta: null,
                R: null,
                distToPrevIP: null,
                distToNextIP: Cartesian3.magnitude(v2),
                tInSolved: 0,
                tOutSolved: 0,
            };
        }
        return null;
    });
    // Pass 2: Overlap Detection and Scaling
    for (let i = 1; i < numIps - 1; i++) {
        const curr = ipGeometries[i];
        const next = ipGeometries[i + 1];
        if (!curr)
            continue;
        let T_curr = curr.R * Math.tan(curr.delta / 2);
        // Forward Overlap (with next curve)
        if (next) {
            const distBetween = curr.distToNextIP;
            const T_next = next.R * Math.tan(next.delta / 2);
            if (T_curr + T_next > distBetween) {
                const solver = solveExactSimpleCurveOverlap(distBetween, curr.R, curr.delta, next.R, next.delta);
                curr.R = solver.R1;
                next.R = solver.R2;
                T_curr = curr.R * Math.tan(curr.delta / 2);
            }
        }
        // Backward Overlap Catch (In case it spills preceding bounds natively or from chain resolving)
        if (T_curr > curr.distToPrevIP) {
            curr.R = curr.distToPrevIP / Math.tan(curr.delta / 2);
            T_curr = curr.distToPrevIP;
        }
        curr.tInSolved = T_curr;
        curr.tOutSolved = T_curr; // Pure arcs are symmetric
    }
    // Pass 3: Coordinate Assignment
    const resolvedPointsList = Array(numIps)
        .fill(null)
        .map(() => ({
        tIn: 0,
        tOut: 0,
        stPoint: null,
        tsPoint: null,
        components: [],
    }));
    resolvedPointsList[0].stPoint = { id: ips[0].id, point: ips[0].point };
    resolvedPointsList[numIps - 1].tsPoint = {
        id: ips[numIps - 1].id,
        point: ips[numIps - 1].point,
    };
    for (let i = 1; i < numIps - 1; i++) {
        const geom = ipGeometries[i];
        if (!geom)
            continue;
        const T = geom.tInSolved;
        //check
        if (Math.abs(Cartesian3.magnitude(geom.unit1) - Cartesian3.magnitude(geom.unit2)) > NEAR_ZERO_THRESHOLD) {
            console.error("Unit 1 and Unit 2 distances are not equal and arc is not symmetric", geom.unit1, geom.unit2);
        }
        const bcPoint = Cartesian3.add(geom.ipPoint, Cartesian3.multiplyByScalar(geom.unit1, T, new Cartesian3()), new Cartesian3());
        const ecPoint = Cartesian3.add(geom.ipPoint, Cartesian3.multiplyByScalar(geom.unit2, T, new Cartesian3()), new Cartesian3());
        //check
        if (Math.abs(Cartesian3.distance(geom.ipPoint, bcPoint) -
            Cartesian3.distance(geom.ipPoint, ecPoint)) > NEAR_ZERO_THRESHOLD) {
            console.error("BCPoint and ECPoint distances are not equal and arc is not symmetric");
        }
        resolvedPointsList[i].tIn = T;
        resolvedPointsList[i].tOut = T;
        resolvedPointsList[i].tsPoint = {
            id: generatePointId("BC", geom.ipId),
            point: bcPoint,
        };
        resolvedPointsList[i].stPoint = {
            id: generatePointId("EC", geom.ipId),
            point: ecPoint,
        };
        const curveData = {
            type: CurveDataType.CIRCULAR_SEGMENT,
            startPointId: resolvedPointsList[i].tsPoint.id,
            endPointId: resolvedPointsList[i].stPoint.id,
            anchorPointId: geom.ipId,
        };
        resolvedPointsList[i].components.push(curveData);
    }
    // Pass 4: Continuous Stitching
    for (let i = 0; i < numIps - 1; i++) {
        if (resolvedPointsList[i].components.length > 0) {
            curves.push(...resolvedPointsList[i].components);
        }
        const stInfo = resolvedPointsList[i];
        const tsInfo = resolvedPointsList[i + 1];
        const startPt = stInfo.stPoint?.point || ips[i].point;
        const endPt = tsInfo.tsPoint?.point || ips[i + 1].point;
        const lineDist = Cartesian3.magnitude(Cartesian3.subtract(endPt, startPt, new Cartesian3()));
        if (lineDist > 0.001) {
            // Connect with Linear Segment
            const startId = stInfo.stPoint
                ? i === 0
                    ? ips[i].id
                    : generatePointId("EC", ips[i].id)
                : ips[i].id;
            const endId = tsInfo.tsPoint
                ? i === numIps - 2
                    ? ips[i + 1].id
                    : generatePointId("BC", ips[i + 1].id)
                : ips[i + 1].id;
            const linearSeg = {
                type: CurveDataType.LINEAR,
                startPointId: startId,
                endPointId: endId,
            };
            curves.push(linearSeg);
        }
    }
    if (resolvedPointsList[numIps - 1].components.length > 0) {
        //last curve will always be EP point
        curves.push(...resolvedPointsList[numIps - 1].components);
    }
    if (curves.length > 0) {
        const firstCurve = curves[0];
        if (firstCurve.type === CurveDataType.CIRCULAR_SEGMENT) {
            firstCurve.startPointId = ips[0].id;
            //check
            const endPoint = resolvedPointsList.find((pt) => pt.stPoint?.id === firstCurve.endPointId);
            if (Cartesian3.distance(ips[0].point, ips[1].point) !==
                Cartesian3.distance(endPoint?.stPoint?.point, ips[1].point)) {
                console.error("FirstCurve : TsPoint and StPoint distances are not equal and arc is not symmetric");
            }
        }
        const lastCurve = curves[curves.length - 1];
        if (lastCurve.type === CurveDataType.CIRCULAR_SEGMENT) {
            lastCurve.endPointId = ips[ips.length - 1].id;
            const startPoint = resolvedPointsList.find((pt) => pt.tsPoint?.id === lastCurve.startPointId);
            if (Cartesian3.distance(ips[ips.length - 2].point, ips[ips.length - 1].point) !==
                Cartesian3.distance(startPoint.tsPoint.point, ips[ips.length - 2].point)) {
                console.error("LastCurve : TsPoint and StPoint distances are not equal and arc is not symmetric");
            }
        }
    }
    const newPoints = [];
    for (let i = 1; i < numIps - 1; i++) {
        const geom = ipGeometries[i];
        const pts = resolvedPointsList[i];
        if (!geom)
            continue;
        if (pts.tsPoint) {
            newPoints.push({
                id: pts.tsPoint.id,
                point: pts.tsPoint.point,
            });
        }
        if (pts.stPoint) {
            newPoints.push({
                id: pts.stPoint.id,
                point: pts.stPoint.point,
            });
        }
    }
    const updatedIPs = ips.map((ip, i) => {
        const geom = ipGeometries[i];
        if (geom) {
            return {
                ...ip,
                radius: geom.R,
            };
        }
        return ip;
    });
    //confirm curves is continues by checking ids
    for (let i = 0; i < curves.length - 1; i++) {
        const currentCurve = curves[i];
        const nextCurve = curves[i + 1];
        if (currentCurve.endPointId !== nextCurve.startPointId) {
            nextCurve.startPointId = currentCurve.endPointId;
        }
    }
    return { curves, newPoints, updatedIPs };
}
//# sourceMappingURL=ip-curve-generator.js.map